import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { RouterModule } from '@angular/router';
import { PaymentMethod, PaymentMethods, PaymentMethodsType } from '@cargos/sprintpay-models';
import { Subject, takeUntil } from 'rxjs';
import { PaymentMethodsService } from 'src/app/shared/services';
import { FeatureRestrictionsService } from 'src/app/shared/services/features/feature-restrictions.service';
import { PaymentMethodsHandlerService } from '../../shared/services/payment-methods-handler.service';
import { LogoPaymentMethodComponent } from '../logo-payment-method/logo-payment-method.component';
import { CargoCreditMaterialModule } from './cargo-credit-material-modules.module';

@Component({
    standalone: true,
    selector: 'app-cargo-credit',
    imports: [LogoPaymentMethodComponent, RouterModule, CargoCreditMaterialModule],
    templateUrl: './cargo-credit.component.html',
    styleUrls: ['./cargo-credit.component.scss'],
})
export class CargoCreditComponent implements OnInit, OnDestroy {
    public isAllowedToPayWithCargoCredit: boolean = false;
    public isThereInvoicesInCart?: boolean;
    public isEnoughCredit?: boolean;
    public cargoCredit?: PaymentMethod;
    public PaymentMethodsEnum = PaymentMethods;
    public paymentMethodSelected: PaymentMethodsType | null = null;
    private unsubscribe$: Subject<void> = new Subject<void>();

    /**
     * This variable does not indicate whether the cart is above the threshold.
     * Instead, it serves as a configuration to determine the selected payment method based on the section.
     */
    @Input() aboveThreshold: boolean = false;
    @Output() eventOnSelectCargoCredit: EventEmitter<string> = new EventEmitter<string>();

    constructor(
        private featureRestrictionsService: FeatureRestrictionsService,
        private paymentMethodsService: PaymentMethodsService,
        private paymentMethodsHandlerService: PaymentMethodsHandlerService
    ) {}

    ngOnInit(): void {
        this.subscribeCargoSprintCredit();
        this.subscribeIsAllowedToPayWithCargoCredit();
        this.subscribePaymentMethodSelected();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    subscribeCargoSprintCredit(): void {
        this.paymentMethodsService
            .getPaymentMethodsByType$(PaymentMethods.CARGO_CREDIT)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (cargoCredit: PaymentMethod | undefined) => {
                    this.cargoCredit = cargoCredit;
                },
            });
    }

    subscribeIsAllowedToPayWithCargoCredit(): void {
        this.featureRestrictionsService
            .isAllowedToPayWithCredit$()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((isAllowedToPayWithCargoCredit) => {
                this.isAllowedToPayWithCargoCredit = isAllowedToPayWithCargoCredit.isAllowed;
                this.isEnoughCredit = isAllowedToPayWithCargoCredit.isEnoughCredit;
                this.isThereInvoicesInCart = isAllowedToPayWithCargoCredit.isThereInvoicesInCart;
            });
    }

    selectPaymentMethod(): void {
        this.eventOnSelectCargoCredit.emit(PaymentMethods.CARGO_CREDIT);
    }

    subscribePaymentMethodSelected(): void {
        this.paymentMethodsHandlerService
            .getPaymentMethodSelected$(this.aboveThreshold)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((paymentMethodSelected) => {
                this.paymentMethodSelected = paymentMethodSelected?.method || null;
            });
    }
}
